import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Video, Phone, ThumbsUp, Send, Loader2, Plus, Bot, StickyNote, Radio } from "lucide-react";

const TABS = [
  { id: "forum", label: "Forum", icon: MessageSquare },
  { id: "room", label: "Study Room", icon: Video },
  { id: "call", label: "Call Space", icon: Phone },
];

export default function SubjectDetail({ onOpenAI }) {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const [subject, setSubject] = useState(null);
  const [tab, setTab] = useState("forum");

  useEffect(() => { api.get(`/subjects/${slug}`).then((r) => setSubject(r.data)); }, [slug]);

  const joined = user?.subjects?.includes(slug);
  const toggleJoin = async () => {
    const newSubs = joined ? user.subjects.filter((x) => x !== slug) : [...(user.subjects || []), slug];
    await api.put("/profile", { subjects: newSubs });
    await refresh();
    toast.success(joined ? "Left the room" : `Joined ${subject.name}`);
  };

  if (!subject) return <div className="p-10 text-center text-[color:var(--bee-muted)]">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/subjects" data-testid="back-to-subjects" className="text-sm text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> All subjects
      </Link>

      {/* Subject header */}
      <div className="bee-card p-8 mb-6 flex flex-col sm:flex-row items-start gap-6">
        <div className="w-20 h-20 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center font-display text-4xl font-bold flex-shrink-0">
          {subject.name.charAt(0)}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="bee-chip text-[10px] tracking-wider uppercase">{subject.category}</span>
            <span className="bee-chip text-[10px] tracking-wider uppercase">{subject.level === "highschool" ? "High School" : "University"}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{subject.name}</h1>
          <p className="text-[color:var(--bee-muted)] mt-2 leading-relaxed">{subject.desc}</p>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-auto">
          <button onClick={toggleJoin} data-testid="toggle-join" className={joined ? "bee-btn-secondary" : "bee-btn-primary"}>
            {joined ? "Leave" : "Join subject"}
          </button>
          <button onClick={() => onOpenAI(subject.name)} data-testid="subject-open-ai" className="bee-btn-secondary flex items-center justify-center gap-2">
            <Bot size={14} /> Ask BeeAI
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[color:var(--bee-border)] mb-6 flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[color:var(--bee-primary)] text-[color:var(--bee-text)]"
                : "border-transparent text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)]"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "forum" && <Forum slug={slug} />}
      {tab === "room" && <StudyRoom slug={slug} subject={subject} />}
      {tab === "call" && <CallSpace slug={slug} subject={subject} />}
    </div>
  );
}

// ================ FORUM ================
function Forum({ slug }) {
  const { subscribe } = useSocket();
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [liveHint, setLiveHint] = useState(false);

  const load = () => api.get(`/subjects/${slug}/posts`).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, [slug]);

  // Real-time: listen for new posts in this subject
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((evt) => {
      if (evt.type === "forum:new_post" && evt.subject_slug === slug) {
        setPosts((cur) => {
          if (cur.some((p) => p.id === evt.post.id)) return cur;
          setLiveHint(true);
          setTimeout(() => setLiveHint(false), 2500);
          return [{ ...evt.post, reply_count: 0 }, ...cur];
        });
      }
      if (evt.type === "forum:new_reply" && evt.subject_slug === slug) {
        setPosts((cur) => cur.map((p) => p.id === evt.post_id ? { ...p, reply_count: (p.reply_count || 0) + 1 } : p));
      }
    });
  }, [subscribe, slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newBody.trim()) return;
    setCreating(true);
    try {
      await api.post(`/subjects/${slug}/posts`, { title: newTitle, body: newBody });
      setNewTitle(""); setNewBody(""); setShowNew(false);
      toast.success("Posted to forum");
      load();
    } finally { setCreating(false); }
  };

  const upvote = async (id) => {
    await api.post(`/posts/${id}/upvote`);
    load();
  };

  if (openPost) return <PostThread postId={openPost} onBack={() => { setOpenPost(null); load(); }} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-[color:var(--bee-muted)]">
          <span>{posts.length} discussion{posts.length !== 1 && "s"}</span>
          {liveHint && (
            <span data-testid="forum-live-indicator" className="inline-flex items-center gap-1 text-[color:var(--bee-primary-hover)] font-semibold animate-fade-up">
              <Radio size={12} /> new post
            </span>
          )}
        </div>
        <button onClick={() => setShowNew((s) => !s)} data-testid="new-post-toggle" className="bee-btn-primary text-sm flex items-center gap-2"><Plus size={14} /> New post</button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="bee-card p-5 mb-6 space-y-3" data-testid="new-post-form">
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Post title" data-testid="new-post-title" className="bee-input w-full" required />
          <textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="What's on your mind?" data-testid="new-post-body" rows={4} className="bee-input w-full resize-none" required />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowNew(false)} className="bee-btn-secondary text-sm">Cancel</button>
            <button type="submit" disabled={creating} data-testid="new-post-submit" className="bee-btn-primary text-sm flex items-center gap-2">
              {creating ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Post
            </button>
          </div>
        </form>
      )}

      {posts.length === 0 ? (
        <div className="bee-card p-10 text-center text-[color:var(--bee-muted)]">
          No posts yet. Start the first conversation.
        </div>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li key={p.id} className="bee-card p-5 flex gap-4" data-testid={`post-${p.id}`}>
              <button onClick={() => upvote(p.id)} data-testid={`upvote-${p.id}`} className="flex flex-col items-center gap-1 text-[color:var(--bee-muted)] hover:text-[color:var(--bee-primary-hover)]">
                <ThumbsUp size={18} />
                <span className="text-xs font-bold">{p.upvotes}</span>
              </button>
              <div onClick={() => setOpenPost(p.id)} className="flex-1 cursor-pointer">
                <h3 className="font-display text-lg font-semibold hover:text-[color:var(--bee-primary-hover)]">{p.title}</h3>
                <p className="text-sm text-[color:var(--bee-muted)] mt-1 line-clamp-2">{p.body}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-[color:var(--bee-muted)]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 hex-clip" style={{ background: p.author_color }} />
                    <span>{p.author_name}</span>
                  </div>
                  <span>·</span>
                  <span>{p.reply_count} repl{p.reply_count === 1 ? "y" : "ies"}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PostThread({ postId, onBack }) {
  const { subscribe } = useSocket();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/posts/${postId}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [postId]);

  // Real-time: append new replies for this thread
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((evt) => {
      if (evt.type === "forum:new_reply" && evt.post_id === postId) {
        setData((cur) => cur && !cur.replies.some((r) => r.id === evt.reply.id)
          ? { ...cur, replies: [...cur.replies, evt.reply] }
          : cur);
      }
    });
  }, [subscribe, postId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/posts/${postId}/replies`, { body: reply });
      setReply("");
      load();
    } finally { setSending(false); }
  };

  if (!data) return <div className="text-center py-10 text-[color:var(--bee-muted)]">Loading…</div>;

  return (
    <div>
      <button onClick={onBack} data-testid="thread-back" className="text-sm text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to forum
      </button>
      <article className="bee-card p-6 mb-4">
        <h2 className="font-display text-2xl font-bold mb-2">{data.post.title}</h2>
        <div className="flex items-center gap-2 text-xs text-[color:var(--bee-muted)] mb-4">
          <div className="w-5 h-5 hex-clip" style={{ background: data.post.author_color }} />
          {data.post.author_name}
        </div>
        <p className="whitespace-pre-wrap leading-relaxed">{data.post.body}</p>
      </article>

      <h3 className="font-display text-lg font-semibold mb-3">{data.replies.length} repl{data.replies.length === 1 ? "y" : "ies"}</h3>
      <ul className="space-y-3 mb-6">
        {data.replies.map((r) => (
          <li key={r.id} className="bee-card p-4" data-testid={`reply-${r.id}`}>
            <div className="flex items-center gap-2 text-xs text-[color:var(--bee-muted)] mb-2">
              <div className="w-5 h-5 hex-clip" style={{ background: r.author_color }} />
              {r.author_name}
            </div>
            <p className="text-sm whitespace-pre-wrap">{r.body}</p>
          </li>
        ))}
      </ul>

      <form onSubmit={submit} className="bee-card p-4 flex gap-2" data-testid="reply-form">
        <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..." data-testid="reply-input" className="bee-input flex-1" />
        <button disabled={sending} data-testid="reply-submit" className="bee-btn-primary flex items-center gap-2">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Reply
        </button>
      </form>
    </div>
  );
}

// ================ STUDY ROOM (Jitsi + notes) ================
function StudyRoom({ slug, subject }) {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [adding, setAdding] = useState(false);
  const room = `focusbee-study-${slug}`;

  useEffect(() => { api.get(`/subjects/${slug}/notes`).then((r) => setNotes(r.data)); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setAdding(true);
    try {
      await api.post(`/subjects/${slug}/notes`, { title, body });
      setTitle(""); setBody("");
      const { data } = await api.get(`/subjects/${slug}/notes`);
      setNotes(data);
      toast.success("Note shared");
    } finally { setAdding(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="bee-card p-4 mb-3">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Live Video Study Room · #{subject.slug}</div>
          <iframe
            title="Study Room"
            src={`https://meet.jit.si/${room}#config.startWithVideoMuted=true&config.prejoinPageEnabled=false`}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="jitsi-frame"
            data-testid="jitsi-study-frame"
          />
          <p className="text-xs text-[color:var(--bee-muted)] mt-2">Anyone with this link joins the same room. Share <code className="bg-[color:var(--bee-surface-alt)] px-1">meet.jit.si/{room}</code></p>
        </div>
      </div>
      <div className="space-y-3">
        <div className="bee-card p-5">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><StickyNote size={16} /> Shared notes</h3>
          <form onSubmit={submit} className="space-y-2 mb-4" data-testid="new-note-form">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" data-testid="note-title" className="bee-input w-full text-sm" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share a summary or link..." rows={3} data-testid="note-body" className="bee-input w-full text-sm resize-none" />
            <button disabled={adding} data-testid="note-submit" className="bee-btn-primary text-sm w-full flex items-center justify-center gap-2">
              {adding ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Add note
            </button>
          </form>
          <ul className="space-y-2 max-h-[300px] overflow-y-auto scrollbar-thin">
            {notes.length === 0 && <li className="text-xs text-[color:var(--bee-muted)]">No notes yet.</li>}
            {notes.map((n) => (
              <li key={n.id} className="border-l-2 border-[color:var(--bee-primary)] pl-3" data-testid={`note-${n.id}`}>
                <div className="text-sm font-semibold">{n.title}</div>
                <div className="text-xs text-[color:var(--bee-muted)] whitespace-pre-wrap mt-1">{n.body}</div>
                <div className="text-[10px] text-[color:var(--bee-muted)] mt-1">— {n.author_name}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ================ CALL SPACE ================
function CallSpace({ slug, subject }) {
  const room = `focusbee-call-${slug}`;
  return (
    <div className="bee-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold">Call space</h2>
          <p className="text-sm text-[color:var(--bee-muted)] mt-1">A dedicated audio-first room for quick group calls — perfect for problem-solving sessions.</p>
        </div>
        <span className="bee-chip">#{subject.slug}</span>
      </div>
      <iframe
        title="Call Space"
        src={`https://meet.jit.si/${room}#config.startWithVideoMuted=true&config.startAudioOnly=true&config.prejoinPageEnabled=false`}
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        className="jitsi-frame"
        data-testid="jitsi-call-frame"
      />
    </div>
  );
}
